<script lang="ts">
  import { PropsFileTreeSidebarProps } from "../models/props";
  import FileItemNode from "./FileItemNode.svelte";

  let {
    files,
    currentPath,
    onNavigate,
    onFileClick,
    branch = "master",
    branches = [],
    currentBranch = "main",
    repoName = "",
    onBranchSwitch,
  }: PropsFileTreeSidebarProps = $props();

  let expanded = $state<Set<string>>(new Set());
  let showBranchDropdown = $state(false);

  // Toggle expand/collapse
  function toggle(path: string) {
    const set = new Set(expanded);
    if (set.has(path)) {
      set.delete(path);
      console.log("Collapsed:", path, "Expanded set:", Array.from(set));
    } else {
      set.add(path);
      console.log("Expanded:", path, "Expanded set:", Array.from(set));
    }
    expanded = set;
  }

  // Switch branch
  function switchBranch(branchName: string) {
    if (onBranchSwitch) {
      onBranchSwitch(branchName);
    }
    showBranchDropdown = false;
  }

  // Auto-expand based on currentPath
  $effect(() => {
    if (currentPath.length === 0) return;

    const set = new Set(expanded);
    let changed = false;

    for (let i = 0; i < currentPath.length; i++) {
      const path = currentPath.slice(0, i + 1).join("/");
      if (!set.has(path)) {
        set.add(path);
        changed = true;
      }
    }

    if (changed) {
      expanded = set;
    }
  });
</script>

<div
  class="w-64 border-r border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto"
  style="height: calc(100vh - 200px);"
>
  <div class="p-4">
    <!-- Branch Switcher -->
    <div class="mb-4">
      <div class="relative">
        <button
          onclick={() => showBranchDropdown = !showBranchDropdown}
          class="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
            <span class="font-medium text-sm text-gray-700 dark:text-gray-200">{currentBranch}</span>
          </div>
          <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {#if showBranchDropdown}
          <!-- Backdrop to dismiss dropdown when clicking outside -->
          <div
            class="fixed inset-0 z-0"
            onclick={() => showBranchDropdown = false}
          ></div>

          <div class="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-96 overflow-y-auto">
            <div class="p-2">
              <div class="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Switch branches
              </div>
              {#each branches as branchName}
                <button
                  onclick={() => switchBranch(branchName)}
                  class="w-full text-left px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between group"
                >
                  <span class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                    </svg>
                    {branchName}
                  </span>
                  {#if branchName === currentBranch}
                    <svg class="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                  {/if}
                </button>
              {/each}
            </div>
            <div class="border-t border-gray-200 dark:border-gray-700 p-2">
              <a
                href="#/users/{(() => {
                  const hash = window.location.hash;
                  const match = hash.match(/\/users\/([^\/]+)\//);
                  return match ? match[1] : '';
                })()}/repositories/{repoName}/branches"
                class="block w-full text-left px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-blue-600 dark:text-blue-400"
                onclick={() => showBranchDropdown = false}
              >
                View all branches...
              </a>
            </div>
          </div>
        {/if}
      </div>
    </div>

    <div class="flex items-center justify-between mb-3">
      <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">Files</h3>
      <div class="text-xs text-gray-500 dark:text-gray-400">
        {branches.length} {branches.length === 1 ? 'branch' : 'branches'}
      </div>
    </div>

    <div class="space-y-1">
      {#each files as item}
        <FileItemNode
          {item}
          parentPath={[]}
          {expanded}
          {toggle}
          {currentPath}
          {onFileClick}
        />
      {/each}
    </div>
  </div>
</div>
